import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/auth/AuthModal";

type OAuthResult = {
  redirect_url?: string;
  redirect_to?: string;
  client?: { name?: string } | null;
};

type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<{ data: OAuthResult | null; error: Error | null }>;
  approveAuthorization: (id: string) => Promise<{ data: OAuthResult | null; error: Error | null }>;
  denyAuthorization: (id: string) => Promise<{ data: OAuthResult | null; error: Error | null }>;
};

const oauth = () => (supabase.auth as unknown as { oauth: OAuthApi }).oauth;

export const Route = createFileRoute("/.lovable/oauth/consent")({
  // Browser-only: the Supabase client reads its session from localStorage.
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id: typeof s['authorization_id'] === "string" ? s['authorization_id'] : "",
  }),
  component: Consent,
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-5 rounded-xl border border-border p-6">{children}</div>
    </main>
  );
}

function isExpired(message: string) {
  const m = message.toLowerCase();
  return m.includes("expired") || m.includes("not found") || m.includes("invalid") || m.includes("used");
}

function Consent() {
  const { authorization_id } = Route.useSearch();
  const { isAuthenticated, isLoading, user, signOut } = useAuth();
  const [details, setDetails] = useState<OAuthResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  // Once the reader signs in, this effect re-runs and the pending request
  // continues on its own.
  useEffect(() => {
    if (!authorization_id || !isAuthenticated) return;
    setAuthOpen(false);
    let cancelled = false;
    void (async () => {
      const { data, error: detailsError } = await oauth().getAuthorizationDetails(authorization_id);
      if (cancelled) return;
      if (detailsError) {
        setLoadError(detailsError.message);
        return;
      }
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [authorization_id, isAuthenticated]);

  if (!authorization_id) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold">Invalid connection request</h1>
        <p className="text-sm text-muted-foreground">
          This link is missing its request details. Start the connection again from your AI assistant.
        </p>
      </Shell>
    );
  }

  if (isLoading) {
    return (
      <Shell>
        <p className="text-muted-foreground">Loading...</p>
      </Shell>
    );
  }

  if (!isAuthenticated) {
    return (
      <Shell>
        <h1 className="text-2xl font-bold">Sign in to continue</h1>
        <p className="text-sm text-muted-foreground">
          Sign in to your Byteline account to connect an outside app. You will come straight back here.
        </p>
        <button
          onClick={() => setAuthOpen(true)}
          className="w-full rounded-md bg-primary px-4 py-2 text-primary-foreground"
        >
          Sign in
        </button>
        <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      </Shell>
    );
  }

  if (denied) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold">Request refused</h1>
        <p className="text-sm text-muted-foreground">
          Nothing was connected to your account. You can close this page.
        </p>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold">
          {isExpired(loadError) ? "This request has expired" : "Could not load this request"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isExpired(loadError)
            ? "Connection requests are only valid for a short time. Start the connection again from your AI assistant."
            : loadError}
        </p>
      </Shell>
    );
  }

  const clientName = details?.client?.name ?? "This app";

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const { data, error: decisionError } = approve
      ? await oauth().approveAuthorization(authorization_id)
      : await oauth().denyAuthorization(authorization_id);
    if (decisionError) {
      setBusy(false);
      if (isExpired(decisionError.message)) {
        setLoadError(decisionError.message);
        return;
      }
      setError(decisionError.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      if (!approve) {
        setDenied(true);
        return;
      }
      setError("The sign-in server did not return a return address.");
      return;
    }
    window.location.href = target;
  }

  return (
    <Shell>
      <h1 className="text-2xl font-bold">Connect {clientName} to your account?</h1>
      <p className="text-sm text-muted-foreground">
        {clientName} will be able to search stories, and use your bookmarks and likes, as you on Byteline.
      </p>
      <div className="rounded-md bg-muted px-3 py-2 text-sm">
        <span className="text-muted-foreground">Signed in as </span>
        <span className="font-medium">{user?.email ?? "your account"}</span>
        <button
          onClick={() => void signOut()}
          className="ml-2 text-xs underline text-muted-foreground hover:text-foreground"
        >
          Switch account
        </button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex gap-3">
        <button
          disabled={busy || !details}
          onClick={() => decide(true)}
          className="flex-1 rounded-md bg-primary px-4 py-2 text-primary-foreground disabled:opacity-60"
        >
          Allow
        </button>
        <button
          disabled={busy}
          onClick={() => decide(false)}
          className="flex-1 rounded-md border border-border px-4 py-2 disabled:opacity-60"
        >
          Refuse
        </button>
      </div>
    </Shell>
  );
}
